/* eslint-disable no-param-reassign */
import {
  booleanFriendlyOptions,
  COMPOUNDING_ALIAS,
  COMPOUNDING_PERIOD_ALIAS,
  CUMULATIVE_DIVIDENDS_ALIAS,
  DIVIDEND_RATE_ALIAS,
} from 'common/constants/cap-table';
import { SelectValueViewer } from 'components';
import { GridSelect } from 'components/FeaturedSpreadsheet/components';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { TCells } from 'pages/ValuationsAllocation/approaches/DiscountCashFlow/components/EffectiveTaxRateLedger/types';
import { removeClassName, removeValidationClasses } from 'utilities';
import { MULTIPLE_OF_ALIAS, MULTIPLE_OF_OPTIONS, YEARFRAC_BASIS_ALIAS, YEARFRAC_BASIS_OPTIONS } from './constants';
import { extractColumnLetterSet } from '../../../utilities/variablePropertiesUtils';

const findCellForColumnByAlias = (currentCell: ICell, cells: TCells, aliasToFind: string): ICell | undefined => {
  const currentCellLetter = extractColumnLetterSet(currentCell.key!);
  if (!currentCellLetter) return;
  const desiredCell = Object.values(cells).find((cell: ICell) => {
    const tempCellColumn = extractColumnLetterSet(cell.key!);
    const isSameColumn = tempCellColumn?.startsWith(currentCellLetter);
    const isDesiredCellForColumn = isSameColumn && cell.alias === aliasToFind;
    return isDesiredCellForColumn;
  });

  return desiredCell;
};

const liqPrefLedgerConditions = ({
  cell,
  cells,
  tableData,
  onCellsChanged,
}: {
  cell: ICell;
  cells: { [key: string]: TCells };
  tableData: { isDisabled: boolean };
  onCellsChanged: (cellChanges: { cell: ICell; value: any }[]) => void;
}) => {
  const { isDisabled } = tableData;

  const { LiquidationPreferenceLedger } = cells;
  const { alias, className, columnLegend } = cell;
  // find the has_dividend cell in the current column
  const dividendCellChanged = cell.alias === CUMULATIVE_DIVIDENDS_ALIAS;
  const hasDividends = Boolean(Number(cell?.value));

  const getCellValue = (tempCell: ICell) =>
    tempCell.defaultValue && !Number.isNaN(tempCell.defaultValue)
      ? Number(tempCell.defaultValue)
      : (tempCell.defaultValue ?? '');

  const resetCellConfig = (originalCell: ICell) => {
    Object.assign(originalCell, {
      hidden: true,
      readOnly: true,
      forceComponent: false,
      disableEvents: false,
      component: null,
      dialog: null,
      greaterThan: null,
      isDisabled,
    });
  };

  const setAsRequired = (originalCell: ICell, isRequired = true) => {
    cell.isRequired = isRequired;
    Object.assign(originalCell, {
      isRequired,
    });
  };

  const setAsEditable = (originalCell: ICell) => {
    resetCellConfig(originalCell);
    Object.assign(originalCell, {
      readOnly: isDisabled,
      hidden: false,
    });
  };

  const setAsHidden = (originalCell: ICell, keepValue = false) => {
    resetCellConfig(originalCell);
    if (!keepValue) {
      Object.assign(originalCell, {
        value: getCellValue(originalCell),
        expr: getCellValue(originalCell),
      });
    }

    Object.assign(originalCell, {
      hidden: true,
      tooltipMessages: null,
      isRequired: false,
      className: removeClassName(removeValidationClasses(className ?? ''), 'read-only--white'),
    });
  };

  if (alias === CUMULATIVE_DIVIDENDS_ALIAS) {
    cell.hidden = false;
    cell.readOnly = isDisabled;
    cell.dataEditor = (props: any) => <GridSelect options={booleanFriendlyOptions} {...props} />;
    cell.valueViewer = (props: any) => <SelectValueViewer {...props} />;
    cell.isRequired = true;
  }

  if (alias === MULTIPLE_OF_ALIAS) {
    cell.readOnly = isDisabled;
    cell.dataEditor = (props: any) => <GridSelect {...props} options={MULTIPLE_OF_OPTIONS} usePortal />;
    cell.valueViewer = (props: any) => <SelectValueViewer {...props} options={MULTIPLE_OF_OPTIONS} />;
  }

  if (alias === YEARFRAC_BASIS_ALIAS) {
    cell.readOnly = isDisabled;
    cell.dataEditor = (props: any) => <GridSelect {...props} options={YEARFRAC_BASIS_OPTIONS} usePortal />;
    cell.valueViewer = (props: any) => <SelectValueViewer {...props} options={YEARFRAC_BASIS_OPTIONS} />;
  }

  if (cell.alias === COMPOUNDING_ALIAS) {
    const isCompounding = Boolean(Number(cell.value));
    const compoundingPeriodCell = findCellForColumnByAlias(cell, LiquidationPreferenceLedger, COMPOUNDING_PERIOD_ALIAS);
    if (!compoundingPeriodCell) return cell;

    if (isCompounding) {
      setAsEditable(compoundingPeriodCell);
    } else {
      setAsHidden(compoundingPeriodCell);
    }
  }

  const adjustableCellAliases = [COMPOUNDING_ALIAS, DIVIDEND_RATE_ALIAS, COMPOUNDING_PERIOD_ALIAS];

  if (dividendCellChanged) {
    // find the desired aliases and set config based on if it was true or not
    const cellsToChange: ICell[] = Object.values(LiquidationPreferenceLedger).filter(
      (tCell: ICell) => adjustableCellAliases.includes(tCell.alias ?? '') && tCell.columnLegend === columnLegend
    );

    const cellChanges: { cell: ICell; value: any }[] = [];

    cellsToChange.forEach((tCell: ICell) => {
      if (tCell.alias === COMPOUNDING_PERIOD_ALIAS) {
        if (hasDividends) {
          setAsEditable(tCell);
        } else {
          setAsHidden(tCell);
          cellChanges.push({
            cell: tCell,
            value: tCell.value,
          });
        }
      }
      if (tCell.alias === DIVIDEND_RATE_ALIAS) {
        if (hasDividends) {
          setAsEditable(tCell);
        } else {
          setAsHidden(tCell);
          cellChanges.push({
            cell: tCell,
            value: tCell.value,
          });
        }
      }

      if (tCell.alias === COMPOUNDING_ALIAS) {
        if (hasDividends) {
          setAsEditable(tCell);
          setAsRequired(tCell);
          tCell.dataEditor = (props: any) => <GridSelect options={booleanFriendlyOptions} {...props} />;
          tCell.valueViewer = props => <SelectValueViewer {...props} />;
          tCell.skipInitialValidation = true;
        } else {
          setAsHidden(tCell);
          cellChanges.push({
            cell: tCell,
            value: tCell.value,
          });
        }
      }

      if (cellChanges.length) {
        onCellsChanged(cellChanges);
      }
    });
  }

  return cell;
};

export default liqPrefLedgerConditions;
